/**
 * Shapes of the Academy JSON fields that the OpenAPI schema leaves open (`unknown`): the player
 * state, results, review and the exam format. Read defensively: a missing field never crashes.
 */

import type { FalseFriendHit } from "@agencyos/api-client";
import { type CoFlow, type ExamPicture, type SpokenLine, parseCoFlow, parsePicture, parsePictures, parseSpokenLines } from "@agencyos/core";

import { parseFalseFriends } from "./falseFriends";

export type Skill = "co" | "ce" | "ee" | "eo";
export const SKILLS: Skill[] = ["co", "ce", "ee", "eo"];
export const MCQ_SKILLS: Skill[] = ["co", "ce"];

export type Localized = string | Record<string, string> | null | undefined;

export interface StateQuestion {
  id: string;
  index: number;
  level: string;
  kind: string;
  document: string;
  /** CO without a generated audio: the lines for the device's voices (D-106). */
  script_for_speech: SpokenLine[];
  has_audio: boolean;
  stem: string;
  options: string[];
  /** CO: instruction, reading and answer times of the official flow (null: older server). */
  flow: CoFlow | null;
  /** The picture of the question (CO kind « image »: « Regardez l'image »), or null. */
  image: ExamPicture | null;
  /** One picture per option (usually empty), null where an option has none. */
  option_images: (ExamPicture | null)[];
}

/** A CE question in free navigation: the learner's current choice comes with it (or null). */
export interface NavQuestion extends StateQuestion {
  choice: number | null;
}

export interface TaskLimits {
  words?: [number, number];
  parts?: Record<string, [number, number]>;
  prep_seconds?: number;
  seconds?: number;
  label?: Localized;
}

export interface TaskDocument {
  title?: string;
  text: string;
}

export interface StateTask {
  id: string;
  number: number;
  prompt: string;
  documents: TaskDocument[];
  limits: TaskLimits;
  text: string;
  status: string;
  has_audio: boolean;
  /** EO: the examiner's recorded follow-ups, said when the learner stays silent (tasks 1 and 3, task 2 without the live examiner, D-134). */
  follow_ups: string[];
  /** EO task 2: the examiner answers each turn live (POST …/examiner/, P3B-06). */
  live: boolean;
}

export interface SkillResult {
  score?: number;
  cefr?: string;
  nclc?: number;
  right?: number;
  questions?: number;
  pending?: boolean;
  /** D-171: planned tasks left empty (they count 0), out of the planned tasks. */
  unanswered?: number;
  tasksPlanned?: number;
}

export type Results = Partial<Record<Skill, SkillResult>> & { beginner?: boolean };

export interface PlayerState {
  status: string;
  /** mock, practice, placement…: the placement hides the level of its questions (D-173). */
  kind: string;
  section: Skill | "";
  remaining: number;
  skills: Skill[];
  format: Record<string, unknown>;
  question: StateQuestion | null;
  /** CE of a mock or practice: every question is sent, the learner moves freely until time up. */
  navigable: boolean;
  questions: NavQuestion[];
  answered: number;
  total: number;
  tasks: StateTask[];
  results: Results;
}

type Json = Record<string, unknown>;

export function isObject(value: unknown): value is Json {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : typeof value === "number" ? String(value) : fallback;
}

export function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.map((v) => str(v)) : [];
}

function pair(value: unknown): [number, number] | undefined {
  return Array.isArray(value) && value.length === 2 ? [num(value[0]), num(value[1])] : undefined;
}

export function isSkill(value: unknown): value is Skill {
  return typeof value === "string" && (SKILLS as string[]).includes(value);
}

/** A name stored as {fr, en} (or a plain string) → the text of the interface language. */
export function localized(value: unknown, language: string): string {
  if (typeof value === "string") return value;
  if (!isObject(value)) return "";
  const lang = language.split("-")[0] ?? "fr";
  return str(value[lang]) || str(value.fr) || str(Object.values(value)[0]);
}

function parseQuestion(value: unknown): StateQuestion | null {
  if (!isObject(value)) return null;
  return {
    id: str(value.id),
    index: num(value.index),
    level: str(value.level),
    kind: str(value.kind),
    document: str(value.document),
    script_for_speech: parseSpokenLines(value.script_for_speech),
    has_audio: value.has_audio === true,
    stem: str(value.stem),
    options: strings(value.options),
    flow: parseCoFlow(value.flow),
    image: parsePicture(value.image),
    option_images: parsePictures(value.option_images),
  };
}

function parseDocuments(value: unknown): TaskDocument[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((doc): TaskDocument =>
      isObject(doc)
        ? { title: str(doc.title) || str(doc.source) || undefined, text: str(doc.text) || str(doc.body) }
        : { text: str(doc) },
    )
    .filter((doc) => doc.text);
}

function parseLimits(value: unknown): TaskLimits {
  if (!isObject(value)) return {};
  const parts: Record<string, [number, number]> = {};
  if (isObject(value.parts)) {
    for (const [key, range] of Object.entries(value.parts)) {
      const p = pair(range);
      if (p) parts[key] = p;
    }
  }
  return {
    words: pair(value.words),
    parts: Object.keys(parts).length ? parts : undefined,
    prep_seconds: typeof value.prep_seconds === "number" ? value.prep_seconds : undefined,
    seconds: typeof value.seconds === "number" ? value.seconds : undefined,
    label: isObject(value.label) || typeof value.label === "string" ? (value.label as Localized) : undefined,
  };
}

function parseTask(value: unknown): StateTask | null {
  if (!isObject(value)) return null;
  const script = isObject(value.examiner) ? value.examiner : isObject(value.examiner_script) ? value.examiner_script : {};
  return {
    id: str(value.id),
    number: num(value.number),
    prompt: str(value.prompt),
    documents: parseDocuments(value.documents),
    limits: parseLimits(value.limits),
    text: str(value.text),
    status: str(value.status),
    has_audio: value.has_audio === true,
    follow_ups: strings(script.follow_ups).filter(Boolean),
    live: script.live === true,
  };
}

export function parseResults(value: unknown): Results {
  const out: Results = {};
  if (!isObject(value)) return out;
  if (value.beginner === true) out.beginner = true;
  for (const skill of SKILLS) {
    const row = value[skill];
    if (!isObject(row)) continue;
    out[skill] = {
      score: typeof row.score === "number" ? row.score : undefined,
      cefr: str(row.cefr) || undefined,
      nclc: typeof row.nclc === "number" ? row.nclc : undefined,
      right: typeof row.right === "number" ? row.right : undefined,
      questions: typeof row.questions === "number" ? row.questions : undefined,
      pending: row.pending === true,
      unanswered: num(row.unanswered),
      tasksPlanned: num(row.tasks_planned),
    };
  }
  return out;
}

export function parseState(value: unknown): PlayerState {
  const data = isObject(value) ? value : {};
  const section = data.section;
  return {
    status: str(data.status),
    kind: str(data.kind),
    section: isSkill(section) ? section : "",
    remaining: num(data.remaining_seconds),
    skills: Array.isArray(data.skills) ? data.skills.filter(isSkill) : [],
    format: isObject(data.format) ? data.format : {},
    question: parseQuestion(data.question),
    navigable: data.navigable === true,
    questions: Array.isArray(data.questions)
      ? data.questions
          .map((q): NavQuestion | null => {
            const base = parseQuestion(q);
            if (!base || !isObject(q)) return null;
            return { ...base, choice: typeof q.choice === "number" ? q.choice : null };
          })
          .filter((q): q is NavQuestion => q !== null)
      : [],
    answered: num(data.answered),
    total: num(data.total),
    tasks: Array.isArray(data.tasks)
      ? data.tasks.map(parseTask).filter((t): t is StateTask => t !== null)
      : [],
    results: parseResults(data.results),
  };
}

export interface ReviewQuestion {
  skill: string;
  level: string;
  stem: string;
  document: string;
  options: string[];
  choice: number;
  answer: number;
  correct: boolean;
  explanation: string;
  /** CO, once the sitting is over (D-106): the audio to hear again and the transcript. */
  audioUrl: string | null;
  transcript: SpokenLine[];
  image: ExamPicture | null;
  optionImages: (ExamPicture | null)[];
}

export interface GradeCriterion {
  key: string;
  points: number;
  comment: string;
}

export interface GradeError {
  excerpt: string;
  correction: string;
  explanation: string;
}

export interface Grade {
  criteria: GradeCriterion[];
  errors: GradeError[];
  corrected: string;
  model: string;
  advice: string[];
  warning: string;
  comment: string;
  /** English false friends and calques found in the text (English speakers, D-094). */
  falseFriends: FalseFriendHit[];
}

export interface ReviewProduction {
  skill: string;
  task: number;
  prompt: string;
  text: string;
  wordCount: number;
  status: string;
  score: number | null;
  grade: Grade;
  /** D-174: the recording is no longer kept; the transcript and the correction stay. */
  audioExpired?: boolean;
  /** D-171: nothing written or said: « non traitée », 0. */
  unanswered: boolean;
  id: string;
  rawGrade: unknown;
  /** EO: signed link to the learner's own recording (listen again), or null. */
  audioUrl: string | null;
}

export interface Review {
  results: Results;
  questions: ReviewQuestion[];
  productions: ReviewProduction[];
}

export function parseGrade(value: unknown): Grade {
  const g = isObject(value) ? value : {};
  return {
    criteria: (Array.isArray(g.criteria) ? g.criteria : []).filter(isObject).map((c) => ({
      key: str(c.key),
      points: num(c.points),
      comment: str(c.comment),
    })),
    errors: (Array.isArray(g.errors) ? g.errors : []).filter(isObject).map((e) => ({
      excerpt: str(e.excerpt),
      correction: str(e.correction),
      explanation: str(e.explanation),
    })),
    corrected: str(g.corrected_version),
    model: str(g.model_answer),
    advice: strings(g.advice).filter(Boolean),
    warning: str(g.word_warning),
    comment: str(g.comment),
    falseFriends: parseFalseFriends(g.false_friends),
  };
}

export function parseReview(value: unknown): Review {
  const data = isObject(value) ? value : {};
  return {
    results: parseResults(data.results),
    questions: (Array.isArray(data.questions) ? data.questions : []).filter(isObject).map((q) => ({
      skill: str(q.skill),
      level: str(q.level),
      stem: str(q.stem),
      document: str(q.document),
      options: strings(q.options),
      choice: num(q.choice, -1),
      answer: num(q.answer, -1),
      correct: q.correct === true,
      explanation: str(q.explanation),
      audioUrl: str(q.audio_url) || null,
      transcript: parseSpokenLines(q.transcript),
      image: parsePicture(q.image),
      optionImages: parsePictures(q.option_images),
    })),
    productions: (Array.isArray(data.productions) ? data.productions : [])
      .filter(isObject)
      .map((p) => ({
        skill: str(p.skill),
        task: num(p.task),
        prompt: str(p.prompt),
        text: str(p.text),
        wordCount: num(p.word_count),
        status: str(p.status),
        score: typeof p.score === "number" ? p.score : null,
        grade: parseGrade(p.grade),
        unanswered: isObject(p.grade) && p.grade.unanswered === true,
        id: str(p.id),
        rawGrade: p.grade,
        audioUrl: str(p.audio_url) || null,
        audioExpired: p.audio_expired === true,
      })),
  };
}

export interface RubricRow {
  key: string;
  max: number;
  label: Localized;
}

/** Rubric criteria labels by skill, from the exam format (`/academy/format/`). */
export function parseRubrics(value: unknown): Record<string, RubricRow[]> {
  const out: Record<string, RubricRow[]> = {};
  if (!isObject(value)) return out;
  for (const [skill, rows] of Object.entries(value)) {
    if (!Array.isArray(rows)) continue;
    out[skill] = rows.filter(isObject).map((r) => ({
      key: str(r.key),
      max: num(r.max),
      label: isObject(r.label) || typeof r.label === "string" ? (r.label as Localized) : undefined,
    }));
  }
  return out;
}

export interface StudyPlan {
  weeks: number;
  hoursPerWeek: number;
  priorities: Skill[];
  mockEveryWeeks: number;
}

export interface Placement {
  results: Results;
  plan: StudyPlan | null;
  at: string;
}

export function parsePlacement(value: unknown): Placement | null {
  if (!isObject(value) || !isObject(value.results)) return null;
  const plan = isObject(value.plan) ? value.plan : null;
  return {
    results: parseResults(value.results),
    plan: plan
      ? {
          weeks: num(plan.weeks),
          hoursPerWeek: num(plan.hours_per_week),
          priorities: Array.isArray(plan.priorities) ? plan.priorities.filter(isSkill) : [],
          mockEveryWeeks: num(plan.mock_every_weeks),
        }
      : null,
    at: str(value.at),
  };
}

/** Maximum of a skill's score scale, read from the exam format (`scale`: [100, 699] or 20). */
export function scaleMax(skill: Skill, sections: unknown): number | null {
  const section = isObject(sections) ? sections[skill] : undefined;
  if (!isObject(section)) return null;
  const scale = section.scale;
  if (typeof scale === "number") return scale;
  const range = pair(scale);
  return range ? range[1] : null;
}
